import React, { useState, useEffect, useRef } from 'react';

export default function Navbar({ lang, setLang, t, onOpenFullscreenChat }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const getLangBadge = () => {
    if (lang === 'Tamil') return 'தமிழ்';
    if (lang === 'Hindi') return 'हिंदी';
    if (lang === 'Telugu') return 'తెలుగు';
    if (lang === 'Kannada') return 'ಕನ್ನಡ';
    if (lang === 'Malayalam') return 'മലയാളം';
    if (lang === 'Arabic') return 'العربية';
    return 'EN';
  };

  const languages = [
    {
      code: 'English',
      label: 'English',
      native: 'English',
      flag: '🇬🇧',
      desc: 'English'
    },
    {
      code: 'Tamil',
      label: 'Tamil',
      native: 'தமிழ்',
      flag: '🇮🇳',
      desc: 'தமிழ்'
    },
    {
      code: 'Hindi',
      label: 'Hindi',
      native: 'हिंदी',
      flag: '🇮🇳',
      desc: 'हिंदी'
    },
    {
      code: 'Telugu',
      label: 'Telugu',
      native: 'తెలుగు',
      flag: '🇮🇳',
      desc: 'తెలుగు'
    },
    {
      code: 'Kannada',
      label: 'Kannada',
      native: 'ಕನ್ನಡ',
      flag: '🇮🇳',
      desc: 'ಕನ್ನಡ'
    },
    {
      code: 'Malayalam',
      label: 'Malayalam',
      native: 'മലയാളം',
      flag: '🇮🇳',
      desc: 'മലയാളം'
    },
    {
      code: 'Arabic',
      label: 'Arabic',
      native: 'العربية',
      flag: '🇸🇦',
      desc: 'العربية'
    }
  ];

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsDropdownOpen(false);
    };
    if (isDropdownOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDropdownOpen]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <nav className="site-nav">
      <div className="container nav-container">
        <div className="brand-wrap">
          <div className="brand-icon">👩‍🍳</div>
          <div className="brand-text">
            <h1>MyMenuBuddy</h1>
            <span>{t.siteTagline}</span>
          </div>
        </div>

        <ul className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <li><a href="#hero" onClick={() => setIsMobileMenuOpen(false)}>{t.navHome}</a></li>
          <li><a href="#aiAssistantSection" onClick={() => setIsMobileMenuOpen(false)}>{t.navSimulator}</a></li>
          <li><a href="#recipesSection" onClick={() => setIsMobileMenuOpen(false)}>{t.navRecipes}</a></li>
          <li><a href="#tiffinSection" onClick={() => setIsMobileMenuOpen(false)}>{t.navTiffin || (lang === 'Tamil' ? '🍱 டிபன் பாக்ஸ்' : (lang === 'Hindi' ? '🍱 टिफिन बॉक्स' : '🍱 Tiffin Box'))}</a></li>
          <li><a href="#communitySection" onClick={() => setIsMobileMenuOpen(false)}>{t.navCommunity || 'Community Feed'}</a></li>
          <li><a href="#pantrySection" onClick={() => setIsMobileMenuOpen(false)}>{t.navPantry}</a></li>
          <li><a href="#plannerSection" onClick={() => setIsMobileMenuOpen(false)}>{t.navPlanner}</a></li>
        </ul>

        <div className="nav-actions">
          {/* Mobile Hamburger Toggle */}
          <button
            type="button"
            className="mobile-hamburger-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </button>

          {/* Small Sleek Language Dropdown */}
          <div className="nav-lang-dropdown-container" ref={dropdownRef}>
            <button
              type="button"
              className={`nav-lang-dropdown-btn ${isDropdownOpen ? 'active' : ''}`}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              title={lang === 'Tamil' ? 'மொழியை மாற்றவும்' : (lang === 'Hindi' ? 'भाषा बदलें' : (lang === 'Telugu' ? 'భాషను మార్చండి' : (lang === 'Arabic' ? 'تغيير اللغة' : 'Change Language')))}
              aria-label="Language Selector"
              aria-expanded={isDropdownOpen}
              id="navLangDropdownBtn"
            >
              <span className="nav-lang-globe">🌐</span>
              <span className="nav-lang-current">{getLangBadge()}</span>
              <span className={`nav-lang-arrow ${isDropdownOpen ? 'open' : ''}`}>▾</span>
            </button>

            {isDropdownOpen && (
              <div className="nav-lang-dropdown-menu" role="menu">
                <div className="nav-lang-dropdown-header">
                  <span>{lang === 'Tamil' ? 'மொழி தேர்வு' : (lang === 'Hindi' ? 'भाषा चुनें' : (lang === 'Telugu' ? 'భాషను ఎంచుకోండి' : (lang === 'Arabic' ? 'اختيار اللغة' : 'Choose Language')))}</span>
                </div>
                <div className="nav-lang-options-list">
                  {languages.map((l) => {
                    const isActive = lang === l.code;
                    return (
                      <button
                        key={l.code}
                        type="button"
                        role="menuitem"
                        className={`nav-lang-option ${isActive ? 'active' : ''}`}
                        onClick={() => {
                          setLang(l.code);
                          setIsDropdownOpen(false);
                        }}
                      >
                        <span className="nav-lang-option-flag">{l.flag}</span>
                        <div className="nav-lang-option-names">
                          <span className="nav-lang-native">{l.native}</span>
                          <span className="nav-lang-sub">{l.label}</span>
                        </div>
                        {isActive && (
                          <span className="nav-lang-check-badge">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            className="btn btn-primary btn-sm nav-chat-btn"
            onClick={() => {
              if (onOpenFullscreenChat) {
                onOpenFullscreenChat();
              } else {
                document.getElementById('aiAssistantSection')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            title={t.btnFullscreen || 'Chat Fullscreen'}
          >
            <span>💬</span>
            <span>{t.btnTrySimulator}</span>
          </button>
        </div>
      </div>
    </nav>
  );
}
